import { VehicleReport } from '@ltvco/refresh-lib/v1';
import type { GenericReportResponse } from '@ltvco/refresh-lib/v2';
import { commaFormatCurrency } from '@ltvco/refresh-lib/utils';
import { QuarterlyTrend, VehicleForecast } from '@ltvco/refresh-lib/api';
import { isValidVehicleValue } from '../utils';

export interface QuarterlyTrendWithValue extends QuarterlyTrend {
  value: number;
}

export interface VehicleForecastWithValues
  extends Omit<VehicleForecast, 'trend_by_quarter'> {
  trend_by_quarter: QuarterlyTrendWithValue[];
}

export interface VehicleForecastData {
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  incidents: number;
  owners: number;
  recalls: number;
  thefts: number;
  titleIssues: number;
  marketValue: number;
  mileage?: number;
}

/**
 * Calculates the dollar values for each quarter based on the change_percent and initial market value
 * Formula: value = market_value × (1 + change_percent/100)
 *
 * @param forecast - The forecast data with only change_percent (no values)
 * @param marketValue - The initial market value of the vehicle
 * @returns The forecast data with calculated dollar values for each quarter
 */
export const calculateVehicleForecasterValues = (
  forecast: VehicleForecast,
  marketValue: number,
): VehicleForecastWithValues => {
  if (!forecast.trend_by_quarter || forecast.trend_by_quarter.length === 0) {
    return {
      ...forecast,
      trend_by_quarter: [],
    };
  }

  const trendWithValues: QuarterlyTrendWithValue[] =
    forecast.trend_by_quarter.map((quarter) => {
      // Formula: value = market_value × (1 + change_percent/100)
      // Example: if marketValue = 25000 and change_percent = -8.5
      // value = 25000 × (1 + (-8.5/100)) = 25000 × 0.915 = 22875
      const value = Math.round(
        marketValue * (1 + quarter.change_percent / 100),
      );

      return {
        ...quarter,
        value,
      };
    });

  return {
    ...forecast,
    trend_by_quarter: trendWithValues,
  };
};

export const extractVehicleSearchParams = (
  vehicleArgs: Record<string, any>,
): {
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  licensePlate?: string;
  state?: string;
} => {
  return {
    ...(isValidVehicleValue(vehicleArgs.vehicle_vin) && {
      vin: vehicleArgs.vehicle_vin,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_year) && {
      year: vehicleArgs.vehicle_year,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_make) && {
      make: vehicleArgs.vehicle_make,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_model) && {
      model: vehicleArgs.vehicle_model,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_trim) && {
      trim: vehicleArgs.vehicle_trim,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_license_plate) && {
      licensePlate: vehicleArgs.vehicle_license_plate,
    }),
    ...(isValidVehicleValue(vehicleArgs.state) && { state: vehicleArgs.state }),
  };
};

export const extractVehicleDataFromReport = (
  reportData: GenericReportResponse,
): VehicleForecastData | null => {
  const vehicleReport = new VehicleReport(reportData as never);
  const processedReportData = vehicleReport.data;
  const currentVehicle = processedReportData.automobiles?.[0];

  if (!currentVehicle) {
    return null;
  }

  const accidentsCount = currentVehicle.accidentsCount || 0;
  const damagesCount =
    currentVehicle.damage_history?.reported_damage?.length || 0;

  return {
    year: currentVehicle.year || undefined,
    make: currentVehicle.make || undefined,
    model: currentVehicle.model || undefined,
    trim: currentVehicle.design?.trim || undefined,
    incidents: accidentsCount + damagesCount,
    owners: currentVehicle.ownership_history?.length || 0,
    recalls: currentVehicle.openRecallsCount || 0,
    thefts: currentVehicle.theftsCount || 0,
    titleIssues: currentVehicle.titleChecksCount || 0,
    marketValue: currentVehicle.valuation?.clean_retail?.adjusted || 0,
    mileage: currentVehicle.mileage?.miles || undefined,
  };
};

export const buildForecastData = (
  isFreePlan: boolean,
  vehicleData: VehicleForecastData,
  userMileage?: number,
): string => {
  const forecastData = {
    date: new Date().toISOString(),
    year: vehicleData.year,
    make: vehicleData.make,
    model: vehicleData.model,
    market_value: vehicleData.marketValue,
    mileage: userMileage || vehicleData.mileage || 0,
    ...(vehicleData.trim && { trim: vehicleData.trim }),
    ...(!isFreePlan && {
      incidents: vehicleData.incidents,
      owners: vehicleData.owners,
      recalls: vehicleData.recalls,
      thefts: vehicleData.thefts,
      title_issues: vehicleData.titleIssues,
    }),
  };

  return JSON.stringify(forecastData);
};

/**
 * Extract market value from message
 */
export const extractMarketValueFromMessage = (message: {
  content?: string | null;
}): number => {
  try {
    const content = message.content || '';
    const parsed = JSON.parse(content);
    return parsed.market_value || parsed.marketValue || 0;
  } catch {
    return 0;
  }
};

/**
 * Format Y-axis values in thousands (e.g., 5000 => "5k")
 */
export const formatYAxis = (value: number): string =>
  `$${Math.round(value / 1000)}k`;

/**
 * Calculate "nice" tick intervals based on range
 * Returns intervals like 1k, 2k, 3k, 5k, 10k, etc.
 */
export const getNiceInterval = (raw: number): number => {
  if (raw <= 1000) return 1000;

  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 3) return 3 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
};

/**
 * Calculate dynamic Y-axis ticks with nice intervals
 */
export const calculateDynamicTicks = (
  domainMin: number,
  domainMax: number,
  isMobile: boolean,
): number[] => {
  const totalRange = domainMax - domainMin;
  const numTicks = isMobile ? 3 : 4;
  const rawInterval = totalRange / (numTicks - 1);

  const niceInterval = getNiceInterval(rawInterval);

  // Generate ticks with nice intervals
  const dynamicTicks: number[] = [];
  const startTick = Math.floor(domainMin / niceInterval) * niceInterval;

  for (let tick = startTick; tick <= domainMax; tick += niceInterval) {
    if (tick >= domainMin) {
      dynamicTicks.push(tick);
    }
  }

  // Ensure we have the max value if it's not already there
  if (dynamicTicks[dynamicTicks.length - 1] < domainMax) {
    dynamicTicks.push(domainMax);
  }

  return Array.from(new Set(dynamicTicks)).sort((a, b) => a - b);
};

/**
 * Calculate tooltip position based on selected point
 */
export const calculateTooltipPosition = (
  selectedPointIndex: number | null,
  quarterlyDataLength: number,
  selectedValue: number,
  minValue: number,
  maxValue: number,
): { left: number; top: number } => {
  if (selectedPointIndex === null) {
    return { left: 50, top: 50 };
  }

  const totalPoints = quarterlyDataLength - 1;
  const valueRange = maxValue - minValue;
  if (totalPoints <= 0 || valueRange <= 0) {
    return { left: 50, top: 50 };
  }
  const left = (selectedPointIndex / totalPoints) * 85 + 7.5; // Approximate chart area
  const valuePosition = (selectedValue - minValue) / valueRange;
  const top = 90 - valuePosition * 70; // Invert for chart coordinates

  return { left, top };
};

/**
 * Calculate chart domain with padding
 */
export const calculateChartDomain = (values: number[]) => {
  if (!values.length)
    return { domainMin: 0, domainMax: 1000, minValue: 0, maxValue: 0 };
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const padding = range * 0.1; // 10% padding

  return {
    domainMin: Math.floor((minValue - padding) / 1000) * 1000,
    domainMax: Math.ceil((maxValue + padding) / 1000) * 1000,
    minValue,
    maxValue,
  };
};

/**
 * Helper to format value change as currency
 */
export const formatCurrencyValueChange = (
  marketValue: number,
  changePercent: number,
): string =>
  commaFormatCurrency(Math.round(marketValue * (changePercent / 100)));
